import { useMutation } from '@tanstack/react-query';

import {
  deleteV1OauthClientById,
  postV1OauthClient,
  postV1OauthClientByIdIcon,
  putV1OauthClientById,
} from '../../../api/generated/pap';
import { CustomApplicationPostBody } from '../types';

/**
 * Mutation hooks for the Custom Applications settings page.
 *
 * Each hook wraps the generated SDK call (with `throwOnError` so failures
 * reject). The `If-Match` header for the PUT is attached automatically by the
 * shared request interceptor (`src/api/clients/interceptors.ts`) from the ETag
 * captured by the matching `useCustomApplication` GET. The list is not cleared
 * here: `CustomApplications.tsx` invalidates it when its modals close.
 */

// convert a data URL (data:<mime>;base64,...) to a File
function dataUrlToFile(dataUrl: string, filename = 'file'): File {
  const [meta, b64] = dataUrl.split(',');
  const mimeMatch = meta.match(/data:(.*?);base64/);
  const mime = mimeMatch ? mimeMatch[1] : 'application/octet-stream';
  const binary = atob(b64);
  const len = binary.length;
  const bytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new File([bytes], filename, { type: mime });
}

const uploadIcon = (id: string, iconURL: string) =>
  postV1OauthClientByIdIcon({
    path: { id },
    body: { thumbnail: dataUrlToFile(iconURL, 'logo.png') },
    throwOnError: true,
  });

export const useAddCustomApplication = () =>
  useMutation({
    mutationFn: async (payload: CustomApplicationPostBody) => {
      const { iconURL, ...rest } = payload;

      // create the application first
      const { data: created } = await postV1OauthClient({ body: rest, throwOnError: true });

      // if no icon to upload, return created result
      if (!iconURL) return created;

      // try uploading the icon; if it fails, delete the created app and rethrow
      try {
        await uploadIcon(created.id, iconURL);
        return created;
      } catch (uploadErr) {
        try {
          await deleteV1OauthClientById({ path: { id: created.id }, throwOnError: true });
        } catch (deleteErr) {
          // log delete failure but prefer to surface the original upload error
          // eslint-disable-next-line no-console
          console.error('Failed to delete custom app after icon upload failure', deleteErr);
        }
        throw uploadErr;
      }
    },
  });

export const useUpdateCustomApplication = () =>
  useMutation({
    mutationFn: async ({ id, payload }: { id: string; payload: CustomApplicationPostBody }) => {
      const { iconURL, ...rest } = payload;

      // If iconURL is a data URL -> user selected a new file -> upload multipart
      if (typeof iconURL === 'string' && iconURL.startsWith('data:')) {
        await uploadIcon(id, iconURL);
      }

      const { data } = await putV1OauthClientById({
        path: { id },
        body: rest,
        throwOnError: true,
      });
      return data;
    },
  });

export const useDeleteCustomApplication = () =>
  useMutation({
    mutationFn: async (id: string) => {
      const { data } = await deleteV1OauthClientById({ path: { id }, throwOnError: true });
      return data;
    },
  });
